import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, Edit3, X } from "lucide-react";
import { BuyAdminSubscription, buyAdminApi } from "../../../services/buyAdminApi";
import styles from "./MasterData.module.css";

const emptyForm = {
  subscription_type: "annual",
  amount_dollars: 100,
  duration_days: 365,
  currency: "usd",
  description: "",
};

const formatMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const dollarsToCents = (dollars: number) => Math.round(dollars * 100);
const centsToDollars = (cents: number) => Number((cents / 100).toFixed(2));

const toApiPayload = (form: typeof emptyForm) => ({
  subscription_type: form.subscription_type.trim().toLowerCase(),
  amount_cents: dollarsToCents(form.amount_dollars),
  duration_days: form.duration_days,
  currency: form.currency,
  description: form.description,
});

const SubscriptionPlansSection = ({ refreshKey = 0 }: { refreshKey?: number }) => {
  const [rows, setRows] = useState<BuyAdminSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<BuyAdminSubscription | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await buyAdminApi.getSubscriptions();
      setRows(response.data || []);
    } catch (err: any) {
      setError(err?.message || "Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows, refreshKey]);

  const openEdit = (row: BuyAdminSubscription) => {
    setEditing(row);
    setForm({
      subscription_type: row.subscription_type,
      amount_dollars: centsToDollars(row.amount_cents),
      duration_days: row.duration_days,
      currency: row.currency || "usd",
      description: row.description || "",
    });
  };

  const closeModal = () => {
    setEditing(null);
    setForm({ ...emptyForm });
  };

  const handleSave = async () => {
    if (!form.subscription_type.trim()) {
      setError("Type is required.");
      return;
    }
    if (!Number.isFinite(form.amount_dollars) || form.amount_dollars <= 0) {
      setError("Amount must be greater than $0.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const payload = toApiPayload(form);
      await buyAdminApi.updateSubscription(editing!.id, payload);
      closeModal();
      await loadRows();
    } catch (err: any) {
      setError(err?.message || "Failed to save subscription");
      setLoading(false);
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>Subscription Plans</h2>
      </div>

      {error ? (
        <div className={styles.errorBanner}>
          <AlertCircle size={16} />
          {error}
        </div>
      ) : null}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount</th>
              <th>Duration (days)</th>
              <th>Currency</th>
              <th>Description</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className={styles.capitalize}>{row.subscription_type}</td>
                <td>{formatMoney(row.amount_cents)}</td>
                <td>{row.duration_days}</td>
                <td>{row.currency}</td>
                <td>{row.description || "-"}</td>
                <td>
                  <button type="button" className={styles.iconButton} onClick={() => openEdit(row)}>
                    <Edit3 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && !loading ? (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  No subscription plans found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className={styles.modalBackdrop} role="presentation">
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3>Edit Subscription</h3>
              <button type="button" className={styles.closeIconButton} onClick={closeModal} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <label>
                Type
                <input type="text" value={form.subscription_type} disabled />
              </label>
              <label>
                Amount (USD)
                <input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={form.amount_dollars}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      amount_dollars: Number.parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </label>
              <label>
                Duration (days)
                <input
                  type="number"
                  value={form.duration_days}
                  onChange={(e) => setForm((prev) => ({ ...prev, duration_days: Number(e.target.value) }))}
                />
              </label>
              <label>
                Currency
                <input value={form.currency} onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value }))} />
              </label>
              <label>
                Description
                <input
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              </label>
            </div>
            <div className={styles.modalActions}>
              <button type="button" className={styles.closeButton} onClick={closeModal}>
                Cancel
              </button>
              <button type="button" className={styles.saveButton} onClick={() => void handleSave()} disabled={loading}>
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SubscriptionPlansSection;
